import { useEffect, useState } from "react";
import { ArrowRight, Timer } from "lucide-react";
import { PreviewTag } from "@/components/kit";

const fmt = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
};

const useCountdown = (initial) => {
    const [left, setLeft] = useState(initial);
    useEffect(() => {
        const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, []);
    return left;
};

export const TaskCard = ({ task, onView, onCreate, compact = false }) => {
    const left = useCountdown(task.deadlineSec);

    return (
        <article
            data-testid={`task-card-${task.id}`}
            className="group relative flex h-full flex-col border border-white/[0.08] bg-[#0A1627] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#1677FF]/50 hover:shadow-[0_18px_50px_rgba(0,0,0,0.5)]"
        >
            <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute">TASK #{task.id}</span>
                <span className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 border border-[#31D8FF]/25 bg-[#31D8FF]/[0.06] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#31D8FF]">
                        <span className="h-1 w-1 rounded-full bg-[#31D8FF]" /> {task.status}
                    </span>
                    {!compact && <PreviewTag>PROTOCOL PREVIEW</PreviewTag>}
                </span>
            </div>

            <h3 className="mt-4 font-display text-xl font-bold leading-snug tracking-tightest text-ink">{task.title}</h3>

            <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-5">
                <div>
                    <dt className="label-tech">REWARD</dt>
                    <dd className="mt-1.5 font-display text-2xl font-bold text-[#31D8FF]" data-testid={`task-reward-${task.id}`}>
                        {task.reward} USDC
                    </dd>
                </div>
                <div>
                    <dt className="label-tech">CAPABILITY</dt>
                    <dd className="mt-1.5 font-mono text-xs font-semibold tracking-[0.12em] text-ink">{task.capability}</dd>
                </div>
                <div>
                    <dt className="flex items-center gap-2 label-tech">
                        <Timer size={10} /> DEADLINE
                    </dt>
                    <dd className="mt-1.5 font-mono text-xs font-semibold tabular-nums tracking-[0.12em] text-ink" data-testid={`task-deadline-${task.id}`}>
                        {fmt(left)}
                    </dd>
                </div>
                <div>
                    <dt className="label-tech">VERIFICATION</dt>
                    <dd className="mt-1.5 font-mono text-[10px] font-semibold tracking-[0.16em] text-[#8CB8FF]">{task.verification}</dd>
                </div>
            </dl>

            <div className="mt-7 flex items-center gap-2.5 border-t border-white/[0.06] pt-5">
                <button
                    data-testid={`task-view-${task.id}`}
                    onClick={() => onView?.(task)}
                    className="inline-flex flex-1 items-center justify-center gap-2 border border-white/15 px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-[#31D8FF] hover:text-[#31D8FF]"
                >
                    VIEW TASK <ArrowRight size={12} />
                </button>
                <button
                    data-testid={`task-create-${task.id}`}
                    onClick={() => onCreate?.(task)}
                    className="inline-flex flex-1 items-center justify-center gap-2 bg-[#1677FF] px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-[#3D8BFF]"
                >
                    CREATE TASK
                </button>
            </div>
        </article>
    );
};

export default TaskCard;
